import React, { CSSProperties, ReactNode } from 'react';
import AntdCollapse, { CollapseProps as AntdCollapseProps } from 'antd/lib/collapse';
import { childrenOfType } from '../../../utils';

export declare type CollapsibleType = 'header' | 'disabled';

/**
 * {@docCategory Collapse}
 */
export interface CollapseProps {
   /**
    * If true, Collapse renders as Accordion
    * @defaultvalue false
    */
   accordion?: boolean;

   /**
    * Id of the active panel.
    * @defaultvalue No default value. In accordion mode, it's the key of the first panel
    */
   activePanelId?: string | string[];

   /**
    * Id of the default active panel.
    * @defaultvalue No default value. In accordion mode, it's the key of the first panel
    */
   defaultActivePanelId?: string | string[];

   /**
    * Toggles rendering of the border around the collapse block
    * @defaultvalue false
    */
   bordered?: boolean;

   /**
    * Make the collapse borderless and its background transparent
    * @defaultvalue false
    */
   ghost?: boolean;

   /**
    * Set expand icon position.
    * @defaultvalue 'left'
    */
   expandIconPosition?: 'left' | 'right';

   /**
    * Specify whether the panels of children be collapsible or the trigger area of collapsible
    */
   collapsible?: CollapsibleType;

   /**
    * Callback function executed when active panel is changed.
    * @param panelId - The id of the panel
    */
   onChange?: (panelId: string | string[]) => void;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * The children. Must be instances of CollapsePanel.
    */
   children?: ReactNode;
}

export const Collapse = (props: CollapseProps) => {
   const antdCollapseProps: AntdCollapseProps = {
      accordion: props.accordion,
      defaultActiveKey: props.defaultActivePanelId,
      collapsible: props.collapsible,
      bordered: props.bordered,
      ghost: props.ghost,
      expandIconPosition: props.expandIconPosition,
      onChange: props.onChange,
      className: `uf-collapse ${props.className || ''}`,
      style: props.style,
   };

   if (props.activePanelId) {
      antdCollapseProps.activeKey = props.activePanelId;
   }

   return (
      <AntdCollapse {...antdCollapseProps}>
         {
            childrenOfType<CollapsePanelProps>(props.children, CollapsePanel).map(child => {
               const childProps = child.props as CollapsePanelProps;

               return (
                  <AntdCollapse.Panel
                     key={childProps.panelId}
                     id={childProps.panelId}
                     className={'uf-collapse-panel'}
                     header={childProps.header}
                     collapsible={childProps.collapsible ? childProps.collapsible : (childProps.disabled ? 'disabled' : undefined)}
                     showArrow={childProps.showArrow}
                     extra={childProps.extra}
                     forceRender={childProps.forceRender}
                  >
                     {childProps.children}
                  </AntdCollapse.Panel>
               );
            })
         }
      </AntdCollapse>
   );
};

/**
 * {@docCategory Collapse}
 */
export interface CollapsePanelProps {
   /**
    * The id of the panel.
    */
   panelId: string;

   /**
    * The header for the panel.
    */
   header: ReactNode;

   /**
    * If the panel is disabled.
    * @deprecated Use collapsible='disabled' instead
    */
   disabled?: boolean;

   /**
    * Specify whether the panel be collapsible or the trigger area of collapsible
    */
   collapsible?: CollapsibleType;

   /**
    * If false, panel will not show arrow icon.
    * @defaultvalue true
    */
   showArrow?: boolean;

   /**
    * The extra element in the corner.
    */
   extra?: ReactNode;

   /**
    * Forced render of content on panel, instead of lazy rending after clicking on header.
    * @defaultvalue false
    */
   forceRender?: boolean;

   /**
    * The children.
    */
   children?: ReactNode;
}

export class CollapsePanel extends React.Component<CollapsePanelProps> {
}
